import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  ActionIcon,
  Affix,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Tooltip,
  Transition,
} from "@mantine/core";
import { IconMessageCircle, IconSend, IconX } from "@tabler/icons-react";

import { CHAT_PREFILL_EVENT, ChatPrefillDetail } from "@/constants";
import { useFooterOffset } from "@/hooks/use-footer-offset";
import { trackEvent } from "@/tracking";

const MarkdownBubble = lazy(() =>
  import("./markdown-bubble").then((m) => ({ default: m.MarkdownBubble })),
);

type ChatRole = "user" | "assistant";
type ChatMessage = { role: ChatRole; content: string };
type ToolName = "web_search" | "check_availability" | "submit_contact_request";
type Entry =
  | { kind: "message"; role: ChatRole; content: string; done: boolean }
  | { kind: "tool"; name: ToolName; status: "pending" | "done" };

type StreamEvent =
  | { type: "text"; text: string }
  | { type: "tool_use"; name: ToolName }
  | { type: "message_boundary" }
  | { type: "done" }
  | { type: "error"; message: string };

const MAX_INPUT = 2000;
const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 520;

function collapseEntriesForApi(entries: Entry[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const e of entries) {
    if (e.kind !== "message") continue;
    const last = out[out.length - 1];
    if (last && last.role === e.role) {
      last.content = `${last.content}\n\n${e.content}`;
    } else {
      out.push({ role: e.role, content: e.content });
    }
  }
  return out;
}

function finalizeInProgress(entries: Entry[]): Entry[] {
  return entries.map((e) => {
    if (e.kind === "message" && e.role === "assistant" && !e.done) {
      return { ...e, done: true };
    }
    if (e.kind === "tool" && e.status === "pending") {
      return { ...e, status: "done" };
    }
    return e;
  });
}

export function ChatWidget() {
  const { t, i18n } = useTranslation();
  const bottom = useFooterOffset(20);
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const botFieldRef = useRef<HTMLInputElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!viewportRef.current) return;
    viewportRef.current.scrollTo({
      top: viewportRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [entries, pending, open]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ChatPrefillDetail>).detail;
      if (!detail?.prompt) return;
      setOpen(true);
      trackEvent("Chat Opened", { source: "calendar-prefill" });
      setInput(detail.prompt.slice(0, MAX_INPUT));
      // Wait for the panel transition (180ms) so the textarea is mounted before focusing.
      setTimeout(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }, 220);
    };
    window.addEventListener(CHAT_PREFILL_EVENT, handler);
    return () => window.removeEventListener(CHAT_PREFILL_EVENT, handler);
  }, []);

  const send = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || pending) return;
    if (botFieldRef.current?.value) return;

    const apiMessages = collapseEntriesForApi(entries);
    apiMessages.push({ role: "user", content: trimmed });

    setEntries((prev) => [
      ...prev,
      { kind: "message", role: "user", content: trimmed, done: true },
    ]);
    setInput("");
    setPending(true);
    setError(null);
    trackEvent("Chat Message Sent", {
      language: i18n.language,
      messageIndex: apiMessages.filter((m) => m.role === "user").length,
    });

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          language: i18n.language,
          messages: apiMessages,
          botField: "",
        }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        if (response.status === 429) {
          setError(t("chat.rateLimited"));
        } else {
          setError(t("chat.error"));
        }
        setEntries((prev) => finalizeInProgress(prev));
        return;
      }

      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();
      let buffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { value, done } = await reader.read();
        if (done) {
          streamDone = true;
          break;
        }
        buffer += value;
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";
        for (const event of events) {
          const line = event.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          let payload: StreamEvent;
          try {
            payload = JSON.parse(line.slice(5).trim()) as StreamEvent;
          } catch {
            continue;
          }
          if (payload.type === "text") {
            const delta = payload.text;
            setEntries((prev) => {
              const last = prev[prev.length - 1];
              if (
                last?.kind === "message" &&
                last.role === "assistant" &&
                !last.done
              ) {
                const updated = prev.slice();
                updated[updated.length - 1] = {
                  ...last,
                  content: last.content + delta,
                };
                return updated;
              }
              return [
                ...prev,
                {
                  kind: "message",
                  role: "assistant",
                  content: delta,
                  done: false,
                },
              ];
            });
          } else if (payload.type === "tool_use") {
            const name = payload.name;
            trackEvent("Chat Tool Used", { tool: name });
            setEntries((prev) => [
              ...prev,
              { kind: "tool", name, status: "pending" },
            ]);
          } else if (payload.type === "message_boundary") {
            setEntries((prev) => finalizeInProgress(prev));
          } else if (payload.type === "error") {
            setError(payload.message);
          }
        }
      }
      setEntries((prev) => finalizeInProgress(prev));
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      setError(t("chat.error"));
      setEntries((prev) => finalizeInProgress(prev));
    } finally {
      setPending(false);
      abortRef.current = null;
    }
  }, [input, pending, entries, i18n.language, t]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  const lastEntry = entries[entries.length - 1];
  const showTypingIndicator =
    pending &&
    !(
      lastEntry?.kind === "message" &&
      lastEntry.role === "assistant" &&
      !lastEntry.done
    );

  return (
    <>
      <Affix position={{ bottom, right: 20 }}>
        <Tooltip label={t("chat.openTooltip")} position="left" withArrow>
          <ActionIcon
            size={48}
            radius="xl"
            variant="filled"
            color="sea"
            aria-label={t("chat.openTooltip")}
            onClick={() => {
              setOpen((v) => {
                const next = !v;
                if (next) trackEvent("Chat Opened", { source: "fab" });
                return next;
              });
            }}
          >
            {open ? <IconX size={22} /> : <IconMessageCircle size={22} />}
          </ActionIcon>
        </Tooltip>
      </Affix>

      <Affix position={{ bottom: bottom + 60, right: 20 }}>
        <Transition mounted={open} transition="pop-bottom-right" duration={180}>
          {(styles) => (
            <Paper
              withBorder
              shadow="lg"
              radius="md"
              style={{
                ...styles,
                width: `min(${PANEL_WIDTH}px, calc(100vw - 40px))`,
                height: `min(${PANEL_HEIGHT}px, calc(100dvh - ${bottom + 100}px))`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Group
                justify="space-between"
                p="sm"
                style={{
                  borderBottom: "1px solid var(--mantine-color-default-border)",
                }}
              >
                <Text fw={600} size="sm">
                  {t("chat.title")}
                </Text>
                <ActionIcon
                  variant="subtle"
                  aria-label={t("chat.close")}
                  onClick={() => setOpen(false)}
                >
                  <IconX size={18} />
                </ActionIcon>
              </Group>

              <ScrollArea viewportRef={viewportRef} flex={1} p="sm">
                <Stack gap="sm">
                  {entries.length === 0 && (
                    <Text c="dimmed" size="sm">
                      {t("chat.greeting")}
                    </Text>
                  )}
                  {entries.map((entry, i) =>
                    entry.kind === "tool" ? (
                      <Group
                        key={i}
                        gap="xs"
                        justify="center"
                        px="xs"
                        wrap="nowrap"
                      >
                        {entry.status === "pending" && <Loader size="xs" />}
                        <Text
                          size="xs"
                          c="dimmed"
                          fs="italic"
                          style={{ textAlign: "center" }}
                        >
                          {t(`chat.tool.${entry.name}.${entry.status}`)}
                        </Text>
                      </Group>
                    ) : (
                      <Box
                        key={i}
                        style={{
                          alignSelf:
                            entry.role === "user" ? "flex-end" : "flex-start",
                          maxWidth: "85%",
                        }}
                      >
                        <Paper
                          p="xs"
                          radius="md"
                          bg={
                            entry.role === "user"
                              ? "sea.6"
                              : "var(--mantine-color-default-hover)"
                          }
                          c={entry.role === "user" ? "white" : undefined}
                        >
                          {entry.role === "assistant" && entry.done ? (
                            <Suspense
                              fallback={
                                <Text
                                  size="sm"
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {entry.content}
                                </Text>
                              }
                            >
                              <MarkdownBubble content={entry.content} />
                            </Suspense>
                          ) : (
                            <Text
                              size="sm"
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {entry.content}
                            </Text>
                          )}
                        </Paper>
                      </Box>
                    ),
                  )}
                  {showTypingIndicator && (
                    <Box style={{ alignSelf: "flex-start", maxWidth: "85%" }}>
                      <Paper
                        p="xs"
                        radius="md"
                        bg="var(--mantine-color-default-hover)"
                      >
                        <Text size="sm" c="dimmed">
                          …
                        </Text>
                      </Paper>
                    </Box>
                  )}
                  {error && (
                    <Text size="sm" c="red">
                      {error}
                    </Text>
                  )}
                </Stack>
              </ScrollArea>

              <Box
                p="sm"
                style={{
                  borderTop: "1px solid var(--mantine-color-default-border)",
                }}
              >
                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <input
                    ref={botFieldRef}
                    type="text"
                    name="bot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: -10000,
                      width: 1,
                      height: 1,
                    }}
                  />
                  <Textarea
                    ref={textareaRef}
                    autosize
                    minRows={1}
                    maxRows={4}
                    flex={1}
                    placeholder={t("chat.placeholder")}
                    value={input}
                    onChange={(e) =>
                      setInput(e.currentTarget.value.slice(0, MAX_INPUT))
                    }
                    onKeyDown={handleKeyDown}
                    disabled={pending}
                  />
                  <Button
                    color="sea"
                    onClick={() => void send()}
                    disabled={!input.trim() || pending}
                    aria-label={t("chat.send")}
                    px="sm"
                  >
                    {pending ? (
                      <Loader size="xs" color="white" />
                    ) : (
                      <IconSend size={16} />
                    )}
                  </Button>
                </Group>
              </Box>
            </Paper>
          )}
        </Transition>
      </Affix>
    </>
  );
}

export default ChatWidget;
